'use client';

/**
 * PlanPriceDisplay — the "big price + meta line" block shared by every
 * tier card in the app (public /pricing page and the professional tier
 * upgrade dialog).
 *
 * All VAT math and annual-cycle computation happens in the *caller* —
 * this component is pure presentation. Callers pass the pre-computed
 * display numbers so the component stays provider-agnostic.
 *
 * Two modes, selected by the presence of ``freeLabel``:
 *   - Free: renders just the word ("Free" / "Zadarmo"). No price math.
 *   - Paid: renders ``amount`` + ``currency`` + ``periodLabel`` (e.g.
 *     ``19 EUR /mo``). Optional strikethrough for annual-vs-monthly
 *     comparison, optional savings pill, optional cadence + VAT suffix
 *     meta line.
 */

import { cn } from '@/lib/utils';

// ``| undefined`` is explicit on every optional field so callers can build
// the props object imperatively (``const x = isAnnual ? N : undefined``)
// without tripping ``exactOptionalPropertyTypes: true``. The runtime
// semantics are identical to bare ``?:`` — this is purely a typing lever
// for the project's strict tsconfig.
export interface PlanPriceDisplayProps {
  /**
   * If set, renders just this string and skips all numeric display.
   * Mutually exclusive with ``amount``.
   */
  freeLabel?: string | undefined;

  /** Big price number (already VAT-adjusted by caller). */
  amount?: number | undefined;
  /** e.g. "EUR". Shown after the number in muted weight. */
  currency?: string | undefined;
  /** e.g. "/mo" or "/mes". Shown after the currency. */
  periodLabel?: string | undefined;

  /**
   * Monthly-equivalent struck-through price shown next to a savings
   * badge when the user is on the annual toggle. Caller only passes
   * this when on the annual cycle.
   */
  strikethrough?: number | undefined;
  /** e.g. 17 → rendered as "-17%" savings pill next to strikethrough. */
  savingsPct?: number | undefined;

  /**
   * Meta line under the price: e.g. "billed annually", "billed monthly",
   * or a VAT note like "+ DPH". Caller pre-localizes.
   */
  cadenceLabel?: string | undefined;
  /** e.g. "+ DPH" / "vrátane DPH". Rendered before the cadence label. */
  vatSuffix?: string | undefined;

  className?: string | undefined;
}

/**
 * Formats without trailing zeros for whole numbers, 2 decimals otherwise.
 * Exported so a caller rendering its own compact price block (e.g. a
 * single-row summary card) matches this component's rounding rule instead
 * of re-implementing it.
 */
export function formatAmount(value: number): string {
  return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
}

export function PlanPriceDisplay({
  freeLabel,
  amount,
  currency,
  periodLabel,
  strikethrough,
  savingsPct,
  cadenceLabel,
  vatSuffix,
  className,
}: PlanPriceDisplayProps) {
  const isFree = typeof freeLabel === 'string';

  return (
    <div className={cn('mt-4', className)}>
      <div className='flex items-baseline gap-1.5 transition-all duration-300 ease-out'>
        {isFree ? (
          <span className='text-4xl font-bold text-content-primary'>
            {freeLabel}
          </span>
        ) : (
          <>
            <span
              key={amount}
              className='text-4xl font-bold text-content-primary animate-in fade-in slide-in-from-bottom-2 duration-300'
            >
              {typeof amount === 'number' ? formatAmount(amount) : '—'}
            </span>
            {currency && (
              <span className='text-base text-content-muted font-medium'>
                {currency}
              </span>
            )}
            {periodLabel && (
              <span className='text-sm text-content-muted'>{periodLabel}</span>
            )}
          </>
        )}
      </div>

      {/*
        Meta line — fixed ``min-h`` so toggling monthly ↔ annual doesn't
        jump the whole card. Empty on the free tier.
      */}
      <div className='mt-1 min-h-[2.5rem] transition-all duration-300'>
        {!isFree && typeof strikethrough === 'number' ? (
          <div
            key='annual'
            className='animate-in fade-in slide-in-from-bottom-1 duration-300'
          >
            <div className='flex items-center gap-2'>
              <span className='text-base text-content-muted line-through'>
                {formatAmount(strikethrough)}
                {currency ? ` ${currency}` : ''}
              </span>
              {typeof savingsPct === 'number' && savingsPct > 0 && (
                <span className='text-sm font-bold text-status-success-text bg-status-success-bg px-2 py-0.5 rounded-full'>
                  −{savingsPct} %
                </span>
              )}
            </div>
            {(vatSuffix || cadenceLabel) && (
              <p className='text-xs text-content-muted mt-0.5'>
                {vatSuffix && <>{vatSuffix} &middot; </>}
                {cadenceLabel}
              </p>
            )}
          </div>
        ) : (
          !isFree &&
          (vatSuffix || cadenceLabel) && (
            <p
              key='monthly'
              className='text-sm text-content-muted animate-in fade-in slide-in-from-bottom-1 duration-300'
            >
              {vatSuffix && (
                <span className='text-xs'>{vatSuffix} &middot; </span>
              )}
              {cadenceLabel}
            </p>
          )
        )}
      </div>
    </div>
  );
}
